import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  public_service_provider_api_requests,
  service_provider_api_requests,
  SERVICE_PROVIDER_API,
} from "..";
import { BaseDto, FilterGroup, NormalFilter } from "../../util/baseDto";
import { useExport } from "../../util/useExport";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";
import { ServiceCategoryItem } from "../service-categories";

const PATH = "/keyword";
const BASE_QUERY_KEY = SERVICE_PROVIDER_API + PATH;

export interface SearchKeywordItem extends BaseDto {
  id: number;
  keyword: string;
  serviceCategoryIds: number[];
  categoryName?: string | null;
}

export interface SearchKeywordApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: SearchKeywordItem[];
}

export interface CreateSearchKeywordRequest {
  keyword: string;
  serviceCategoryIds?: number[] | null;
}

export interface UpdateSearchKeywordRequest {
  id: number;
  keyword: string;
  serviceCategoryIds?: number[] | null;
}

export interface CreateSearchKeywordResponse {
  id: number;
  keyword: string;
  serviceCategoryIds: number[];
}

const useCreateSearchKeyword = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (
      request: CreateSearchKeywordRequest,
    ): Promise<CreateSearchKeywordResponse> =>
      service_provider_api_requests.post(PATH, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service keyword created successfully");
    },
    onError: () => {
      setErrorNotification("Failed to create service keyword");
    },
  });
};

const useUpdateSearchKeyword = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number;
      request: UpdateSearchKeywordRequest;
    }): Promise<CreateSearchKeywordResponse> =>
      service_provider_api_requests.put(`${PATH}/${id}`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service keyword updated successfully");
    },
    onError: () => {
      setErrorNotification("Failed to update service keyword");
    },
  });
};

const useSearchServiceKeywords = (
  size: number,
  page: number,
  keyword?: string,
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(keyword ? [NormalFilter("keyword", "LIKE", keyword)] : []),
      ],
    }),
    [keyword],
  );

  return useInfiniteQuery<SearchKeywordApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "paged", keyword, size],
    queryFn: async ({ pageParam = page }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=keyword,asc`,
        filterGroup,
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetAllServiceKeywords = (sort: string) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "all", sort],
    queryFn: async (): Promise<SearchKeywordApiResponse> =>
      public_service_provider_api_requests.get(
        PATH + `/public/all?size=100&sort=${sort}`,
      ),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetServiceKeywords = (keyword: string) => {
  const body: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [NormalFilter("keyword", "LIKE", keyword)],
    }),
    [keyword],
  );

  return useQuery({
    queryKey: [BASE_QUERY_KEY, "search", keyword],
    queryFn: async (): Promise<SearchKeywordApiResponse> =>
      public_service_provider_api_requests.search(
        PATH + "/public/search?page=0&size=100&sort=keyword,asc",
        body,
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!keyword && keyword.trim().length >= 3,
  });
};

const useGetServiceKeywordsByCategory = (category: string) => {
  const body: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [NormalFilter("categories.categoryName", "LIKE", category)],
    }),
    [category],
  );

  return useQuery({
    queryKey: [BASE_QUERY_KEY, "search", "category", category],
    queryFn: async (): Promise<SearchKeywordApiResponse> =>
      public_service_provider_api_requests.search(
        PATH + "/public/search?page=0&size=100&sort=keyword,asc",
        body,
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!category && category.trim().length >= 3,
  });
};

const useGetSearchKeywordById = (id: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "search-keyword", id],
    queryFn: async (): Promise<SearchKeywordItem> =>
      service_provider_api_requests.get(`${PATH}/${id}`),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetServiceChipKeywords = (categoryIds: number[]) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-chips", categoryIds],
    queryFn: async (): Promise<SearchKeywordItem[]> =>
      public_service_provider_api_requests.post(
        `${PATH}/public/service-chips`,
        categoryIds,
      ),
    enabled: categoryIds.length > 0,
    staleTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetAllServiceChipKeywords = () => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-chips", "all"],
    queryFn: async (): Promise<SearchKeywordItem[]> =>
      public_service_provider_api_requests.get(`${PATH}/public/service-chips/all`),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useDeleteSearchKeyword = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      service_provider_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service keyword deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete service keyword");
    },
  });
};

export const useExportServiceKeywords = () =>
  useExport(
    (filterGroup) => public_service_provider_api_requests.export(`${PATH}/public/export`, filterGroup),
    "keywords.xlsx",
    "Failed to export Keywords",
  );

const useGetMatchedCategoriesByKeyword = (keyword: string, city?: string) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "matched-categories", keyword, city],
    queryFn: async (): Promise<ServiceCategoryItem[]> =>
      public_service_provider_api_requests.get(
        `${PATH}/public/matched-categories?keyword=${encodeURIComponent(keyword)}${
          city?.trim() ? `&city=${encodeURIComponent(city)}` : ""
        }`,
      ),
    enabled: !!keyword && keyword.trim().length >= 1,
    staleTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export {
  BASE_QUERY_KEY as SERVICE_KEYWORDS_QUERY_KEY,
  useCreateSearchKeyword,
  useUpdateSearchKeyword,
  useSearchServiceKeywords,
  useGetServiceKeywords,
  useGetAllServiceKeywords,
  useGetSearchKeywordById,
  useDeleteSearchKeyword,
  useGetServiceKeywordsByCategory,
  useGetServiceChipKeywords,
  useGetAllServiceChipKeywords,
  useGetMatchedCategoriesByKeyword,
};
